import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MigrationsStatusBadge from './MigrationsStatusBadge';

const STORAGE_KEY = 'switch_admin_key';

const AdminKeyContext = createContext(null);

/** Hook any child page uses to grab the authenticated admin key. */
export const useAdminKey = () => {
  const ctx = useContext(AdminKeyContext);
  if (!ctx) throw new Error('useAdminKey must be used inside <AdminShell>');
  return ctx;
};

const NAV = [
  { to: '/admin/feedback', label: 'Feedback' },
  { to: '/admin/school-feedback', label: 'School flags' },
  { to: '/admin/schools', label: 'Schools' },
  { to: '/admin/migrations', label: 'Migrations' },
];

/**
 * Shared wrapper for every /admin/* page.
 * - One server-validated login (hits /api/admin/migrations/status).
 * - Key stored in sessionStorage so all admin sub-pages share it.
 * - Sidebar nav + top bar with live MigrationsStatusBadge.
 * - Renders {children} only after successful auth.
 */
const AdminShell = ({ title, subtitle, children, headerRight = null }) => {
  const [key, setKey] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem(STORAGE_KEY) || '';
  });
  const [input, setInput] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Validate a persisted/typed key against the server
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/admin/migrations/status?admin_key=${encodeURIComponent(key)}`
        );
        if (cancelled) return;
        if (res.ok) {
          setAuthenticated(true);
          setError('');
          window.sessionStorage.setItem(STORAGE_KEY, key);
        } else {
          setAuthenticated(false);
          window.sessionStorage.removeItem(STORAGE_KEY);
          if (res.status === 401) setError('Invalid admin key.');
          else setError(`Server error (${res.status}).`);
        }
      } catch {
        if (!cancelled) setError('Network error.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_URL, key]);

  const handleLogout = () => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    setKey('');
    setInput('');
    setAuthenticated(false);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#FEFDF8] flex items-center justify-center px-4">
        <div
          className="bg-white p-6 rounded-2xl shadow-md border border-[#E8DED3] w-full max-w-sm"
          data-testid="admin-shell-login"
        >
          <h2 className="text-xl font-bold text-[#2C2823] mb-1">Admin login</h2>
          <p className="text-sm text-[#6B6560] mb-4">Switch Schools · admin console</p>
          <input
            type="password"
            placeholder="Enter admin key"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.trim()) setKey(input.trim());
            }}
            className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85D35]"
            data-testid="admin-shell-key-input"
          />
          {error && (
            <p className="text-sm text-red-600 mb-3" data-testid="admin-shell-error">
              {error}
            </p>
          )}
          <button
            onClick={() => input.trim() && setKey(input.trim())}
            disabled={loading || !input.trim()}
            className="w-full bg-[#2C2823] hover:bg-[#1A1816] disabled:opacity-60 text-white py-2 rounded-lg text-sm font-semibold"
            data-testid="admin-shell-login-btn"
          >
            {loading ? 'Verifying…' : 'Login'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminKeyContext.Provider value={{ adminKey: key, logout: handleLogout }}>
      <div className="min-h-screen bg-[#FEFDF8] flex" data-testid="admin-shell">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-white border-r border-[#E8DED3] py-6 px-4 hidden md:block">
          <div className="font-black text-[#2C2823] tracking-tight mb-6 px-2">
            Switch · Admin
          </div>
          <nav className="space-y-1">
            {NAV.map((n) => {
              const active = location.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`block px-3 py-2 rounded-lg text-sm ${
                    active
                      ? 'bg-[#FFF5EB] text-[#E85D35] font-semibold'
                      : 'text-[#2C2823] hover:bg-[#F5F1EC]'
                  }`}
                  data-testid={`admin-nav-${n.to.replace('/admin/', '')}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <button
            onClick={handleLogout}
            className="mt-8 w-full text-xs text-[#9B8F88] hover:text-[#2C2823] text-left px-3"
            data-testid="admin-shell-logout"
          >
            Log out
          </button>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          <header className="bg-white border-b border-[#E8DED3] px-6 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-black text-[#2C2823] tracking-tight truncate">
                {title}
              </h1>
              {subtitle && (
                <p className="text-xs sm:text-sm text-[#6B6560] mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {headerRight}
              <MigrationsStatusBadge />
            </div>
          </header>

          <div className="p-6">{children}</div>
        </main>
      </div>
    </AdminKeyContext.Provider>
  );
};

export default AdminShell;
